import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import aiService from '../services/ai.service';
const initialState = {
    thread: null,
    threadLoading: false,
    threadError: null,
    sending: false,
    sendError: null,
    streaming: '',
};
/**
 * ApiCall resolves rather than throws on failure, handing back the AxiosError —
 * so the server's message lives at `.response.data.message`, not `.data.message`.
 * Checking both is what surfaces "no test record for this ad" instead of a
 * generic failure string.
 */
const errorFrom = (res, fallback) => res?.data?.message || res?.response?.data?.message || fallback;
// ── Thunks ─────────────────────────────────────────────────────────────────
export const fetchAiThread = createAsyncThunk('ai/fetchThread', async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await aiService.getThread(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(errorFrom(response, 'Failed to load the AI conversation'));
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to load the AI conversation');
    }
});
export const sendAiMessage = createAsyncThunk('ai/sendMessage', async (payload, { getState, dispatch, rejectWithValue, signal }) => {
    try {
        const token = getState().auth?.token;
        // Streams over SSE and resolves with the payload directly — unlike the
        // ApiCall-based thunks above, it throws on failure rather than
        // resolving with an error response.
        //
        // `signal` is the thunk's own abort signal: calling .abort() on the
        // dispatched promise drops the HTTP connection, which is what tells the
        // server to cancel the turn instead of running it out for nobody.
        return await aiService.sendMessage(payload, token || '', signal, (text) => {
            dispatch(aiStreamDelta(text));
        });
    }
    catch (err) {
        if (signal.aborted)
            return rejectWithValue('cancelled');
        return rejectWithValue(err?.message || 'Failed to generate a response');
    }
});
export const clearAiThread = createAsyncThunk('ai/clearThread', async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await aiService.clearThread(params, token || '');
        if (response?.data?.success)
            return true;
        return rejectWithValue(errorFrom(response, 'Failed to clear the conversation'));
    }
    catch (err) {
        return rejectWithValue(err?.message || 'Failed to clear the conversation');
    }
});
// ── Slice ──────────────────────────────────────────────────────────────────
const aiSlice = createSlice({
    name: 'ai',
    initialState,
    reducers: {
        // Called when the drawer closes so the next ad never flashes the previous
        // ad's conversation while its own thread loads.
        resetAi: () => initialState,
        // One chunk of the reply currently being written.
        aiStreamDelta: (state, action) => {
            state.streaming += action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAiThread.pending, (state) => {
            state.threadLoading = true;
            state.threadError = null;
            state.thread = null;
        })
            .addCase(fetchAiThread.fulfilled, (state, action) => {
            state.threadLoading = false;
            state.thread = action.payload;
        })
            .addCase(fetchAiThread.rejected, (state, action) => {
            state.threadLoading = false;
            state.thread = null;
            state.threadError = action.payload || action.error.message || 'Failed to load the AI conversation';
        })
            .addCase(sendAiMessage.pending, (state) => {
            state.sending = true;
            state.sendError = null;
            state.streaming = '';
        })
            .addCase(sendAiMessage.fulfilled, (state, action) => {
            state.sending = false;
            if (state.thread) {
                // Append the server's canonical pair — the drawer drops its
                // optimistic bubble as soon as these land.
                state.thread.messages.push(action.payload.user_message, action.payload.message);
            }
            // Cleared only now, so the streamed text is replaced by the real
            // message in the same render — no flash of empty space between them.
            state.streaming = '';
        })
            .addCase(sendAiMessage.rejected, (state, action) => {
            state.sending = false;
            state.streaming = '';
            state.sendError = action.payload || action.error.message || 'Failed to generate a response';
        })
            .addCase(clearAiThread.fulfilled, (state) => {
            if (state.thread)
                state.thread.messages = [];
        });
    },
});
export const { resetAi, aiStreamDelta } = aiSlice.actions;
// ── Selectors ──────────────────────────────────────────────────────────────
export const selectAiThread = (s) => s.ai?.thread || null;
export const selectAiContext = (s) => s.ai?.thread?.context || null;
export const selectAiMessages = (s) => s.ai?.thread?.messages || [];
export const selectAiConfigured = (s) => !!s.ai?.thread?.configured;
export const selectAiThreadLoading = (s) => !!s.ai?.threadLoading;
export const selectAiThreadError = (s) => s.ai?.threadError || null;
export const selectAiSending = (s) => !!s.ai?.sending;
export const selectAiStreaming = (s) => s.ai?.streaming || '';
export default aiSlice.reducer;
